// 1. Dependencies
import Swiper from 'swiper/bundle';
import { nodeConfig } from '@/mixins';
import { initial } from 'lodash';

// 2. Configuration
const NAMESPACE = 'swiper';
const DEFAULT_SELECTOR = '.swiper_parent';
const DEFAULT_SETTINGS = {
	slidesPerView: 'auto',
	spaceBetween: 0,
	speed: 500
};

// 3. Module
class SwiperModule {
	constructor(node, config) {
		this.node = node;
		this.config = config;
		this.swiperInstance = null;
		this.onResize = this.onResize.bind(this);
	}

	updateInertAttributes() {
		if (!this.swiperInstance) return;
		const { slides, el: container } = this.swiperInstance;
		const containerRect = container.getBoundingClientRect();

		slides.forEach((slide) => {
			const slideRect = slide.getBoundingClientRect();
			const isVisible =
				slideRect.right > containerRect.left && slideRect.left < containerRect.right;

			if (isVisible) {
				slide.removeAttribute('inert');
				if (!slide.querySelector('a')) {
					slide.setAttribute('tabindex', '0');
				}
			} else {
				slide.setAttribute('inert', 'true');
				slide.setAttribute('tabindex', '-1');
			}
		});
	}

	updateActiveDataset() {
		if (!this.swiperInstance) return;
	
		// realIndex avoids duplicate index issues when loop=true
		const activeIndex = this.swiperInstance.realIndex;
	
		this.node.dataset.activeSlide = activeIndex;
	}			

	bindUI() {
		window.addEventListener('resize', this.onResize);
	}

	bindResetButton() {
		const resetButton = this.node.querySelector('.swiper-button-reset');
		if (!resetButton || !this.swiperInstance) return;
	
		resetButton.addEventListener('click', () => {
			if (this.swiperInstance.params.loop) {
				this.swiperInstance.slideToLoop(0, 0);
			} else {
				this.swiperInstance.slideTo(0, this.swiperInstance.params.speed);
			}
	
			this.node.classList.remove('is-end');
	
			// Ensure accessibility state is correct immediately
			this.updateInertAttributes();
		});
	}	

	checkMaxWidth() {
		const maxWidth = parseInt(
			getComputedStyle(this.node).getPropertyValue('--swiper-max-width'),
			10
		);
		const currentWidth = window.innerWidth;
		return { maxWidth, currentWidth };
	}

	onResize() {
		const { maxWidth, currentWidth } = this.checkMaxWidth();

		if (maxWidth && currentWidth >= maxWidth) {
			if (this.swiperInstance) {
				this.swiperInstance.destroy(true, true);
				this.swiperInstance = null;
				this.node
					.querySelectorAll('[inert]')
					.forEach((slide) => slide.removeAttribute('inert'));
				this.node
					.querySelectorAll('[tabindex]')
					.forEach((slide) => slide.removeAttribute('tabindex'));
			}
		} else {
			if (!this.swiperInstance) {
				this.init();
			} else {
				this.updateInertAttributes();
			}
		}
	}

	updateEndState() {
		if (!this.swiperInstance) return;
	
		// Only apply if navigation buttons exist
		// if (!this.node.querySelector('.swiper-button-reset')) return;
	
		if (this.swiperInstance.isEnd) {
			this.node.classList.add('is-end');
		} else {
			this.node.classList.remove('is-end');
		}

		if (this.swiperInstance.isBeginning) {
			this.node.classList.add('is-beginning');
		} else {
			this.node.classList.remove('is-beginning');
		}
	}	

	init() {
		const { maxWidth, currentWidth } = this.checkMaxWidth();

		if (!maxWidth || currentWidth < maxWidth) {
			const swiperElement = this.node.querySelector('.swiper');
		
			const hasInitialSlide = this.node.dataset.initialSlide !== undefined;
			const initialSlide = hasInitialSlide ? parseInt(this.node.dataset.initialSlide, 10) : 0;
			const shouldAutoOffset = this.node.hasAttribute('data-swiper-offset');
		
			const applyAutoOffset = () => {
				if (!shouldAutoOffset || !this.swiperInstance) {
					return;
				}
		
				const swiper = this.swiperInstance;
				const slides = Array.from(swiper.slides).filter((slide) => {
					return !slide.classList.contains('swiper-slide-duplicate');
				});
		
				if (!slides.length) {
					swiper.params.slidesOffsetAfter = 0;
					swiper.update();
					return;
				}
		
				const lastSlide = slides[slides.length - 1];
				const slideStyles = window.getComputedStyle(lastSlide);
		
				const lastSlideWidth =
					lastSlide.offsetWidth +
					(parseFloat(slideStyles.marginLeft) || 0) +
					(parseFloat(slideStyles.marginRight) || 0);
		
				const containerWidth = swiper.width;
				const offset = Math.max(0, containerWidth - lastSlideWidth);
		
				swiper.params.slidesOffsetAfter = offset;
				swiper.update();
			};
		
			this.swiperInstance = new Swiper(swiperElement, {
				initialSlide: initialSlide,
				keyboard: {
					enabled: true,
					onlyInViewport: true
				},
				a11y: {
					slideRole: false,
					containerRole: 'region'
				},
				centeredSlides: (
					this.node.classList.contains('swiper--centered') &&
					(window.innerWidth < 980 || this.node.classList.contains('swiper--centered-always'))
				),
				slidesPerView: swiperElement.dataset.mobileCount || this.config.slidesPerView,
				spaceBetween: this.config.spaceBetween,
				speed: this.config.speed,
				slidesOffsetAfter: 0,
				navigation: {
					nextEl: this.node.querySelector('.swiper-button-next'),
					prevEl: this.node.querySelector('.swiper-button-prev')
				},
				loop: this.node.classList.contains('swiper--loop'),
				pagination: {
					el: this.node.querySelector('.swiper-pagination'),
					type: 'bullets',
					clickable: true
				},
				breakpoints: this.config.breakpoints,
				on: {
					init: () => {
						applyAutoOffset();
		
						this.updateInertAttributes();
						this.updateEndState();
		
						const initialSlideAttr = this.node.dataset.initialSlide;
		
						if (initialSlideAttr !== undefined) {
							const initialSlide = parseInt(initialSlideAttr, 10);
		
							requestAnimationFrame(() => {
								this.swiperInstance.slideTo(initialSlide + 1, 0, false);
								this.swiperInstance.slideTo(initialSlide, 0, false);
							});
						}
		
						requestAnimationFrame(() => {
							this.updateActiveDataset();
						});
					},
					resize: () => {
						applyAutoOffset();
						this.updateEndState();
					},
					slideChangeTransitionStart: () => {
						this.updateActiveDataset();
						this.updateEndState();
					},
					slideChangeTransitionEnd: () => {
						this.updateInertAttributes();
						this.updateEndState();
					},
					reachEnd: () => {
						this.updateEndState();
					},
					fromEdge: () => {
						this.updateEndState();
					}
				}
			});
		
			this.updateInertAttributes();
			this.updateEndState();
			this.bindResetButton();
		}

		this.bindUI();
		this.onResize();
	}
}

// 4. Factory Function
export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	let listNode = document.querySelectorAll(selector);
	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		let config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		let module = new SwiperModule(node, config);
		module.init();
		return module;
	});
}
